import React, { useEffect, useState } from 'react';
import type { User, PathwayBlock, PathwayAuthorisation } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, CheckCircle, Clock, Award } from 'lucide-react';

interface AuthorisationBlockViewProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
}

export const AuthorisationBlockView: React.FC<AuthorisationBlockViewProps> = ({ user, block, pathwayId }) => {
  const { t } = useTranslation();
  const [authorisation, setAuthorisation] = useState<PathwayAuthorisation | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const config = block.authorisationConfig;

  useEffect(() => {
    const load = async () => {
      try {
        const auth = await DataService.getPathwayAuthorisation(user.id, pathwayId, block.id);
        setAuthorisation(auth);

        if (auth) {
          await DataService.setBlockProgress({
            candidateId: user.id,
            pathwayId,
            blockId: block.id,
            status: 'complete',
            completedAt: auth.authorisedAt,
          });
        }
      } catch (e) {
        console.error('Failed to load authorisation', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, block.id, pathwayId]);

  const roleLabels = config?.authorizedRoles?.map(r => {
    if (r === 'custom' && config.customRoleLabel) return config.customRoleLabel;
    return r.replace(/_/g, ' ');
  }) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      {/* Block header */}
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-aero-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-aero-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">{t('candidate.block.authorisation.title')}</p>
            <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
            {block.description && <p className="text-sm text-aero-600 mt-1">{block.description}</p>}
          </div>
        </div>
      </div>

      {/* Status */}
      {authorisation ? (
        <div className="bg-green-50 rounded-xl border-2 border-green-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-6 h-6 text-green-500" />
            <div>
              <p className="text-sm font-semibold text-green-700">{t('candidate.block.authorisation.authorised')}</p>
              <p className="text-xs text-green-600 mt-0.5">
                {t('candidate.block.authorisation.authorisedBy', { name: authorisation.authoriserName })} · {new Date(authorisation.authorisedAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {authorisation.notes && (
            <div className="bg-white/70 rounded-lg p-3 mb-3">
              <p className="text-xs font-semibold text-aero-600 mb-1">{t('candidate.block.authorisation.notes')}</p>
              <p className="text-sm text-aero-700">{authorisation.notes}</p>
            </div>
          )}

          {authorisation.signatureData && (
            <div className="bg-white/70 rounded-lg p-3">
              <p className="text-xs font-semibold text-aero-600 mb-2">{t('candidate.block.authorisation.signature')}</p>
              <img src={authorisation.signatureData} alt="Signature" className="max-h-20 mx-auto" />
            </div>
          )}

          {authorisation.pathwayDataReview && (
            <div className="bg-white/70 rounded-lg p-3 mt-3">
              <p className="text-xs font-semibold text-aero-600 mb-2">{t('candidate.block.authorisation.summary')}</p>
              <div className="flex items-center justify-between text-xs text-aero-600 mb-1">
                <span>{t('candidate.block.authorisation.blocksCompleted')}</span>
                <span className="font-semibold">{authorisation.pathwayDataReview.completedBlocks} / {authorisation.pathwayDataReview.totalBlocks}</span>
              </div>
              {authorisation.pathwayDataReview.ojtHoursLogged != null && (
                <div className="flex items-center justify-between text-xs text-aero-600">
                  <span>{t('candidate.block.authorisation.ojtHours')}</span>
                  <span className="font-semibold">{authorisation.pathwayDataReview.ojtHoursLogged}h</span>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex items-center justify-center gap-2 py-3 bg-green-100 text-green-700 rounded-xl font-semibold text-sm">
            <CheckCircle className="w-4 h-4" />
            {t('candidate.block.authorisation.pathwayComplete')}
          </div>
        </div>
      ) : (
        <div className="bg-aero-50 rounded-xl border-2 border-aero-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-aero-400" />
            <div>
              <p className="text-sm font-semibold text-aero-700">{t('candidate.block.authorisation.pending')}</p>
              <p className="text-xs text-aero-500 mt-0.5">{t('candidate.block.authorisation.pendingDesc')}</p>
            </div>
          </div>

          {roleLabels.length > 0 && (
            <div className="bg-white/70 rounded-lg p-3">
              <p className="text-xs font-semibold text-aero-600 mb-1">{t('candidate.block.authorisation.requiredRole')}</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {roleLabels.map((label, i) => (
                  <span key={i} className="text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-aero-100 text-aero-600">
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
